import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../../lib/supabase/supabaseClient";
import { useSessionContext } from "../../../lib/contexts/SessionContext";
import { useProfileContext } from "../../../lib/contexts/ProfileContext";
import { Logger } from "../../../lib/logger/Logger";
import { TechnicianSignIn } from "./signin-components/technician-signin.component";
import { DentistSignIn } from "./signin-components/dentist-signin.component";
import { VendorSigninPage } from "../../../dentlab/src/pages/auth/vendor-signin.page";
import { useCentralStore } from "../../../dentlab/src/store/Central";
import { useCentralStore as useConnectCentralStore } from "../../../dentconnect/src/store/Central";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { useAttributesStore } from "../../../dentlab/src/store/Attributes";
import { MIGRATION_DECISION } from "../../../dentlab/src/constants/onboarding";
import { RolesEnum } from "../../../lib/types/roles.types";

export const SignIn: React.FC<{
	isConnectLogin: boolean;
	fallbackUrl: string;
}> = ({ isConnectLogin, fallbackUrl }) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const isVendorLogin = !isConnectLogin && searchParams.get("login") === "kommunikation";
	const {
		setSession: legacySession,
		setLoading,
		loading,
	} = useSessionContext();
	const { fetchProfile } = useProfileContext();
	const initialize = useCentralStore((state) => state.initialize);
	const connectInitialize = useConnectCentralStore((state) => state.initialize);

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (isConnectLogin) setLoading(true);

		const formData: any = new FormData(event.currentTarget);

		try {
			const { data, error } = await supabase.auth.signInWithPassword({
				email: formData.get("email") as string,
				password: formData.get("password") as string,
			});

			if (data.session) {
				Logger.log("Set session after sign in");
				legacySession({
					userId: data.session.user.id,
					userEmail: data.session.user.email,
					isConnectUser: isConnectLogin,
				});
				await initialize();

				// Check where to redirect the user based on onboarding status
				const role = useCentralStore.getState().role;
				if (role === RolesEnum.OWNER) {
					// If chose migration but not completed, go to migration page
					const migrationDecision = useAttributesStore.getState().getOnboardingAttribute('onboarding_migration_decision');
					if (migrationDecision != MIGRATION_DECISION.NO_MIGRATION && migrationDecision != MIGRATION_DECISION.MIGRATION_DONE) {
						navigate('/migration');
						fetchProfile(data.session.user.id);
						return;
					}
				}

				// Default: go to dashboard
				navigate('/');
				fetchProfile(data.session.user.id);
			}
			if (error) {
				Logger.error(error, {}, "Error signing in");
				alert("Falsche Anmeldedaten. Bitte versuchen Sie es erneut.");
			}
		} catch (error: any) {
			showNotification({
				message: "Falscher Benutzername oder Passwort",
				type: "error",
			});
		} finally {
			setLoading(false);
			Logger.log("Sign in complete");
		}
	};

	const handleVendorLoginSuccess = async (data: any) => {
		await initialize();
		fetchProfile(data.session.user.id);
		legacySession({
			userId: data.session.user.id,
			userEmail: data.session.user.email,
			isConnectUser: false,
		});
	};

	if (loading && isConnectLogin) {
		return <div>Lädt...</div>;
	}

	if (isVendorLogin) {
		return <VendorSigninPage onLoginSuccess={handleVendorLoginSuccess} />;
	}

	return (
		<>
			{isConnectLogin ? (
				<DentistSignIn
					onSignIn={async (data: any) => {
						await connectInitialize();
						legacySession({
							userId: data.session.user.id,
							userEmail: data.session.user.email,
							isConnectUser: isConnectLogin,
						});
						fetchProfile(data.session.user.id);
					}}
					fallbackUrl={fallbackUrl}
				/>
			) : (
				<TechnicianSignIn onSubmit={handleSubmit} />
			)}
		</>
	);
};
